import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useDarkMode } from "@/App";
import { ArrowLeft, MapPin, RefreshCw, Trophy, Calendar, Video, Moon, Sun } from "lucide-react";
import { toast } from "sonner";

const MatchDetail = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMatchDetail();
  }, [matchId]);

  const fetchMatchDetail = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/sports/match/${matchId}`);
      if (response.data.status === "success" && response.data.data) {
        setMatch(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching match details:", error);
      toast.error("Failed to load match details");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return dateString; }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-primary)" }} data-testid="match-detail-loading">
        <div className="w-10 h-10 border-3 border-[#00bf63]/20 border-t-[#00bf63] rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!match) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-primary)" }} data-testid="match-not-found">
        <div className="text-center rounded-2xl p-8 shadow-sm max-w-md" style={{ background: "var(--bg-card)" }}>
          <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ background: "var(--bg-card-inner)" }}>
            <Trophy className="w-10 h-10" style={{ color: "var(--text-muted)" }} />
          </div>
          <h3 className="text-xl font-semibold mb-2" style={{ color: "var(--text-primary)" }}>Match Not Found</h3>
          <p className="mb-6" style={{ color: "var(--text-secondary)" }}>The match details could not be loaded.</p>
          <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 px-6 py-3 bg-[#00bf63] text-white font-medium rounded-full hover:bg-[#00a854]" data-testid="back-home-btn">
            <ArrowLeft className="w-4 h-4" />Back to Home
          </button>
        </div>
      </div>
    );
  }

  const isCricketData = match.source === "cricketdata";

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-primary)" }} data-testid="match-detail-page">
      {/* Header */}
      <header className="backdrop-blur-xl border-b sticky top-0 z-50" style={{ background: "var(--header-bg)", borderColor: "var(--header-border)" }}>
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/')} className="p-2 rounded-full transition-colors" style={{ color: "var(--text-secondary)" }} data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <span className="text-lg font-semibold text-[#00bf63]">ScoreXprt</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={toggleDarkMode} className="p-2 rounded-full" style={{ color: "var(--text-secondary)" }} data-testid="detail-dark-mode-toggle">
                {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
              <button onClick={fetchMatchDetail} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#00bf63] hover:bg-[#00bf63]/10 rounded-full" data-testid="refresh-btn">
                <RefreshCw className="w-4 h-4" />Refresh
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="rounded-3xl shadow-sm overflow-hidden" style={{ background: "var(--bg-card)", border: "1px solid var(--border-color)" }} data-testid="match-detail-card">
          {/* Header Bar */}
          <div className="bg-gradient-to-r from-[#00bf63] to-[#00a854] p-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {match.leagueBadge && <img src={match.leagueBadge} alt="" className="w-10 h-10 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />}
                <div>
                  <p className="text-sm font-medium text-white/80 uppercase tracking-wider" data-testid="match-league">{match.league || match.matchType || "Cricket"}</p>
                  {match.round && <p className="text-xs text-white/50">Round {match.round}</p>}
                </div>
              </div>
              {match.status && (
                <span className="px-4 py-1.5 rounded-full text-sm font-medium bg-white/20 text-white" data-testid="match-status-badge">
                  {match.status}
                </span>
              )}
            </div>
          </div>

          <div className="p-6 sm:p-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-center mb-8" style={{ color: "var(--text-primary)" }} data-testid="match-title">{match.name}</h1>

            {/* Cricket scores from cricketdata.org */}
            {isCricketData && match.score && match.score.length > 0 ? (
              <div className="space-y-3 mb-8">
                {match.score.map((inning, idx) => {
                  const parts = inning.inning?.match(/^(.+?)\s*(inning[s]?\s*\d*)/i);
                  const team = parts ? parts[1].trim() : inning.inning;
                  const label = parts ? parts[2].trim() : "";
                  return (
                    <div key={idx} className="rounded-xl p-5" style={{ background: "var(--bg-card-inner)" }}>
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-semibold text-base" style={{ color: "var(--text-primary)" }}>{team}</p>
                          {label && <p className="text-xs uppercase tracking-wide mt-0.5" style={{ color: "var(--text-muted)" }}>{label}</p>}
                        </div>
                        <div className="text-right">
                          <p className="text-3xl font-bold text-[#00bf63]">
                            {inning.r}{inning.w && inning.w !== "-" ? <span className="text-xl" style={{ color: "var(--text-muted)" }}>/{inning.w}</span> : null}
                          </p>
                          {inning.o && inning.o !== "-" && <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{inning.o} overs</p>}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {/* Status text */}
                {match.statusText && (
                  <div className="rounded-xl p-4 text-center" style={{ background: "rgba(0,191,99,0.08)" }}>
                    <p className="text-sm font-medium text-[#00bf63]">{match.statusText}</p>
                  </div>
                )}
              </div>
            ) : (
              /* TheSportsDB scores */
              <div className="rounded-2xl p-6 mb-8" style={{ background: "var(--bg-card-inner)" }} data-testid="match-scores">
                <div className="flex items-center justify-between">
                  <div className="flex-1 text-center">
                    {match.homeTeamBadge && <img src={match.homeTeamBadge} alt="" className="w-16 h-16 mx-auto mb-3 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />}
                    <p className="text-lg font-semibold mb-2" style={{ color: "var(--text-primary)" }} data-testid="home-team-name">{match.homeTeam}</p>
                    <p className="text-4xl sm:text-5xl font-bold text-[#00bf63]" data-testid="home-team-score">
                      {match.homeScore !== null && match.homeScore !== undefined ? match.homeScore : "-"}
                    </p>
                  </div>
                  <div className="px-6">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: "var(--border-color)" }}>
                      <span className="text-lg font-bold" style={{ color: "var(--text-muted)" }}>VS</span>
                    </div>
                  </div>
                  <div className="flex-1 text-center">
                    {match.awayTeamBadge && <img src={match.awayTeamBadge} alt="" className="w-16 h-16 mx-auto mb-3 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />}
                    <p className="text-lg font-semibold mb-2" style={{ color: "var(--text-primary)" }} data-testid="away-team-name">{match.awayTeam}</p>
                    <p className="text-4xl sm:text-5xl font-bold text-[#00bf63]" data-testid="away-team-score">
                      {match.awayScore !== null && match.awayScore !== undefined ? match.awayScore : "-"}
                    </p>
                  </div>
                </div>
                {match.homeScore && match.awayScore && match.status?.toLowerCase().includes("finished") && (
                  <div className="mt-6 pt-6 text-center" style={{ borderTop: "1px solid var(--border-color)" }} data-testid="match-winner">
                    <p className="text-sm" style={{ color: "var(--text-muted)" }}>Winner</p>
                    <p className="text-xl font-bold text-[#00bf63]">
                      {parseInt(match.homeScore) > parseInt(match.awayScore) ? match.homeTeam :
                       parseInt(match.awayScore) > parseInt(match.homeScore) ? match.awayTeam : "Match Tied"}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Match Info */}
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {match.date && (
                <div className="flex items-center gap-4 p-4 rounded-xl" style={{ background: "var(--bg-card-inner)" }} data-testid="match-date-info">
                  <div className="w-12 h-12 bg-[#00bf63]/10 rounded-full flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-[#00bf63]" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Date</p>
                    <p className="font-medium" style={{ color: "var(--text-primary)" }}>{formatDate(match.date)}</p>
                  </div>
                </div>
              )}
              {match.venue && (
                <div className="flex items-center gap-4 p-4 rounded-xl" style={{ background: "var(--bg-card-inner)" }} data-testid="match-venue-info">
                  <div className="w-12 h-12 bg-[#00bf63]/10 rounded-full flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-[#00bf63]" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Venue</p>
                    <p className="font-medium" style={{ color: "var(--text-primary)" }}>
                      {match.venue}{match.city ? `, ${match.city}` : ""}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {match.description && match.description !== "Night Match" && (
              <div className="rounded-xl p-5 mb-8" style={{ background: "rgba(59,130,246,0.06)" }} data-testid="match-description">
                <p className="text-sm font-semibold text-blue-500 uppercase tracking-wider mb-2">Match Summary</p>
                <p className="leading-relaxed" style={{ color: "var(--text-secondary)" }}>{match.description}</p>
              </div>
            )}

            {match.video && (
              <a href={match.video} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 rounded-xl mb-8 transition-colors" style={{ background: "rgba(239,68,68,0.06)" }} data-testid="match-video">
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center"><Video className="w-6 h-6 text-white" /></div>
                <div>
                  <p className="text-sm font-semibold text-red-500 uppercase tracking-wider">Video Highlights</p>
                  <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Watch on YouTube</p>
                </div>
              </a>
            )}

            {(match.poster || match.thumbnail) && (
              <div className="rounded-xl overflow-hidden mb-8" data-testid="match-image">
                <img src={match.poster || match.thumbnail} alt={match.name} className="w-full h-auto object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 text-center">
          <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium text-white bg-[#00bf63] rounded-full hover:bg-[#00a854] shadow-lg" data-testid="back-all-matches-btn">
            <ArrowLeft className="w-4 h-4" />Back to All Matches
          </button>
        </div>
      </main>
    </div>
  );
};

export default MatchDetail;
